import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import toast from 'react-hot-toast';
import { Button } from '~/components/ui/buttons/Button';
import { Input } from '~/components/ui/inputs/Input';
import { SectionPanel } from '~/components/ui/SectionPanel';
import {
  Table,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import {
  isInProgress,
  useDownloadUfpInventoryRunRawResponse,
  useStartUfpInventoryRun,
  useUfpInventoryRun,
  useUfpInventoryRunResult,
  type FollowedUfpInventoryRun,
  type UfpInventoryRun,
  type UfpInventoryRunItem,
  type UfpInventoryRunLocation,
} from '~/api/ufpInventoryRun';
import { formatDateTime, formatDecimal } from '~/utils';
import {
  DEFAULT_RUN_REQUEST,
  RUN_REQUEST_LIMITS,
  validateRunRequest,
  type RunRequestErrors,
  type RunRequestForm,
} from './runRequest';

const RUN_PARAM = 'run';
const NAMESPACE = 'ufp-inventory-run';

const quantity = (value: number | null | undefined): string =>
  value === null || value === undefined ? '' : formatDecimal(value);

function RunStatus({ run }: Readonly<{ run: UfpInventoryRun }>) {
  const { t } = useTranslation(NAMESPACE);

  switch (run.status) {
    case 'QUEUED':
      return <p className="text-sm text-neut-sec">{t('run.queued')}</p>;
    case 'RUNNING':
      return (
        <p className="text-sm text-neut-sec">
          {t('run.running', {
            startedAt: run.startedAt ? formatDateTime(run.startedAt) : '',
          })}
        </p>
      );
    case 'SUCCEEDED':
      return (
        <p className="text-sm text-success-600">
          {t('run.succeeded', {
            finishedAt: run.finishedAt ? formatDateTime(run.finishedAt) : '',
          })}
        </p>
      );
    case 'FAILED':
      return (
        <div className="flex flex-col gap-2">
          <p className="text-sm text-error-600">
            {run.ufpHttpStatus
              ? t('run.failedWithStatus', { status: run.ufpHttpStatus })
              : t('run.failedWithoutStatus')}
          </p>
          {run.failureDetail && (
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded border border-neut-border bg-neut-bg-alt p-3 text-xs font-mono text-neut-sec">
              {run.failureDetail}
            </pre>
          )}
        </div>
      );
  }
}

function productLengthOf(
  item: UfpInventoryRunItem,
  t: TFunction<typeof NAMESPACE>
): string {
  if (item.productName && item.lengthName) {
    return t('table.productLength', {
      productName: item.productName,
      lengthName: item.lengthName,
    });
  }
  return item.productName ?? '';
}

function LocationItems({
  items,
  index,
}: Readonly<{ items: UfpInventoryRunItem[]; index: number }>) {
  const { t } = useTranslation(NAMESPACE);
  const tableControls = useTable({});

  const columns: TableColumn[] = [
    { key: 'productCode', header: t('table.productCode'), width: 9 },
    { key: 'description', header: t('table.description'), width: 18 },
    { key: 'length', header: t('table.length'), width: 7 },
    { key: 'outcome', header: t('table.outcome'), width: 13 },
    {
      key: 'platformProductLength',
      header: t('table.platformProductLength'),
      width: 18,
    },
    { key: 'availablePcs', header: t('table.availablePcs'), width: 7 },
    { key: 'inboundPcs', header: t('table.inboundPcs'), width: 7 },
    { key: 'onOrderPcs', header: t('table.onOrderPcs'), width: 7 },
    { key: 'requestedPcs', header: t('table.requestedPcs'), width: 7 },
    {
      key: 'avgWeeklyUsagePcs',
      header: t('table.avgWeeklyUsagePcs'),
      width: 7,
    },
  ];

  const rows: TableRow[] = items.map((item, itemIndex) => ({
    id: { value: `${index}-${itemIndex}` },
    productCode: { value: item.productCode ?? '' },
    description: { value: item.description ?? '' },
    length: {
      value:
        (item.length ?? '').trim() === ''
          ? t('table.blankLength')
          : (item.length ?? ''),
    },
    outcome: { value: t(`outcome.${item.outcome}`) },
    platformProductLength: { value: productLengthOf(item, t) },
    availablePcs: { value: quantity(item.availablePcs) },
    inboundPcs: { value: quantity(item.inboundPcs) },
    onOrderPcs: { value: quantity(item.onOrderPcs) },
    requestedPcs: { value: quantity(item.requestedPcs) },
    avgWeeklyUsagePcs: { value: quantity(item.avgWeeklyUsagePcs) },
  }));

  return (
    <Table
      minWidth={80}
      rows={rows}
      columns={columns}
      tableControls={tableControls}
      emptyMessage={t('result.noItems')}
    />
  );
}

function LocationResult({
  location,
  index,
}: Readonly<{ location: UfpInventoryRunLocation; index: number }>) {
  const { t } = useTranslation(NAMESPACE);
  const [showsItems, setShowsItems] = useState(index === 0);

  return (
    <SectionPanel
      title={t('result.locationTitle', {
        branchCode: location.branchCode ?? '',
        locationCode: location.locationCode ?? '',
      })}
      aside={
        location.asOfTimestampUtc ? (
          <span className="text-xs text-neut-sec">
            {t('result.asOf', {
              asOf: formatDateTime(location.asOfTimestampUtc),
            })}
          </span>
        ) : undefined
      }
      bodyClassName=""
    >
      <div
        className="px-4 py-3 text-sm text-neut-sec"
        data-testid={`ufp-run-location-${index}`}
      >
        {location.matchedLocations.length === 0 ? (
          t('result.unmatchedLocation')
        ) : (
          <>
            <span className="font-medium text-neut-prim">
              {t('result.matchedLocations')}:{' '}
            </span>
            {location.matchedLocations
              .map((matched) =>
                t('result.matchedLocation', {
                  name: matched.name,
                  organizationName: matched.organizationName,
                })
              )
              .join(', ')}
          </>
        )}
      </div>
      <div className="flex items-center gap-3 border-t border-neut-border px-4 py-2">
        <span className="text-sm text-neut-sec">
          {t('result.itemCount', { count: location.items.length })}
        </span>
        {location.items.length > 0 && (
          <Button
            size="sm"
            width="auto"
            variant="secondary"
            onClick={() => setShowsItems((shown) => !shown)}
            data-testid={`ufp-run-location-toggle-${index}`}
          >
            {showsItems ? t('result.hideItems') : t('result.showItems')}
          </Button>
        )}
      </div>
      {showsItems && location.items.length > 0 && (
        <LocationItems items={location.items} index={index} />
      )}
    </SectionPanel>
  );
}

function RunResult({ runId }: Readonly<{ runId: string }>) {
  const { t } = useTranslation(NAMESPACE);
  const result = useUfpInventoryRunResult(runId);

  switch (result.kind) {
    case 'error':
      return <p className="text-sm text-error-600">{t('result.loadError')}</p>;
    case 'loading':
      return <p className="text-sm text-neut-sec">{t('result.loading')}</p>;
    case 'unreadable':
      return <p className="text-sm text-error-600">{t('result.unreadable')}</p>;
    case 'locations':
      if (result.locations.length === 0) {
        return <p className="text-sm text-neut-sec">{t('result.empty')}</p>;
      }
      return (
        <>
          <p className="text-sm text-neut-sec">{t('result.blankLengthNote')}</p>
          {result.locations.map((location, index) => (
            <LocationResult
              key={`${location.branchCode}-${location.locationCode}-${index}`}
              location={location}
              index={index}
            />
          ))}
        </>
      );
  }
}

function FollowedRun({
  followed,
  runId,
}: Readonly<{ followed: FollowedUfpInventoryRun; runId: string }>) {
  const { t } = useTranslation(NAMESPACE);
  const { download, isDownloading } = useDownloadUfpInventoryRunRawResponse(
    () => toast.error(t('toast.downloadError'))
  );

  switch (followed.kind) {
    case 'idle':
      return null;
    case 'loading':
      return <p className="text-sm text-neut-sec">{t('run.loading')}</p>;
    case 'loadError':
      return <p className="text-sm text-error-600">{t('run.loadError')}</p>;
    case 'notFound':
      return <p className="text-sm text-error-600">{t('run.notFound')}</p>;
    case 'run': {
      const { run } = followed;
      const hasSucceeded = run.status === 'SUCCEEDED';
      return (
        <>
          <SectionPanel
            title={t('run.title')}
            aside={
              hasSucceeded ? (
                <Button
                  size="sm"
                  width="auto"
                  variant="secondary"
                  loading={isDownloading}
                  onClick={() => download(runId)}
                  data-testid="ufp-run-download"
                >
                  {t('run.download')}
                </Button>
              ) : undefined
            }
          >
            <div className="flex flex-col gap-2" data-testid="ufp-run-status">
              <p className="text-sm text-neut-prim">
                {t('run.parameters', {
                  branchCodes: run.branchCodes.join(', '),
                  weeksToLookBack: run.weeksToLookBack,
                  apiVersion: run.apiVersion,
                })}
              </p>
              <RunStatus run={run} />
              {followed.isRefreshFailing && (
                <p
                  className="text-sm text-warning-600"
                  data-testid="ufp-run-refresh-failing"
                >
                  {t('run.refreshFailing')}
                </p>
              )}
            </div>
          </SectionPanel>
          {hasSucceeded && <RunResult runId={runId} />}
        </>
      );
    }
  }
}

function isRunInProgress(followed: FollowedUfpInventoryRun): boolean {
  return (
    followed.kind === 'loading' ||
    (followed.kind === 'run' && isInProgress(followed.run.status))
  );
}

export default function AdminUfpInventory() {
  const { t } = useTranslation(NAMESPACE);
  const [searchParams, setSearchParams] = useSearchParams();
  const runId = searchParams.get(RUN_PARAM) ?? undefined;

  const [form, setForm] = useState<RunRequestForm>(DEFAULT_RUN_REQUEST);
  const [errors, setErrors] = useState<RunRequestErrors>({});

  const { mutate: startRun, isPending: isStarting } = useStartUfpInventoryRun();
  const followed = useUfpInventoryRun(runId);
  const runInProgress = isRunInProgress(followed);

  const updateField =
    (field: keyof RunRequestForm) => (_: unknown, value: string) =>
      setForm((previous) => ({ ...previous, [field]: value }));

  const errorFor = (field: keyof RunRequestForm): string | undefined => {
    const key = errors[field];
    return key === undefined ? undefined : t(key, RUN_REQUEST_LIMITS);
  };

  const handleRun = () => {
    const validated = validateRunRequest(form);
    setErrors(validated.errors);
    if (validated.input === null) return;

    startRun(
      { input: validated.input },
      {
        onSuccess: (data) =>
          setSearchParams({ [RUN_PARAM]: data.startUfpInventoryRun.id }),
        onError: () => toast.error(t('toast.startError')),
      }
    );
  };

  return (
    <div className="w-full flex flex-col gap-6">
      <div>
        <h1 className="text-xl font-semibold text-neut-prim">{t('title')}</h1>
        <p className="text-sm text-neut-sec mt-1">{t('subtitle')}</p>
      </div>

      <SectionPanel title={t('form.title')}>
        <div className="flex flex-wrap items-start gap-3">
          <div className="w-80">
            <Input
              name="ufpRunBranchCodes"
              label={t('form.branchCodes')}
              placeholder={t('form.branchCodesPlaceholder')}
              value={form.branchCodes}
              onChange={updateField('branchCodes')}
              error={errorFor('branchCodes')}
              required
              data-testid="ufp-run-branch-codes"
            />
            <p className="mt-1 text-xs text-neut-sec">
              {t('form.branchCodesHint', RUN_REQUEST_LIMITS)}
            </p>
          </div>
          <div className="w-44">
            <Input
              name="ufpRunWeeksToLookBack"
              label={t('form.weeksToLookBack')}
              type="number"
              min={1}
              max={RUN_REQUEST_LIMITS.maxWeeksToLookBack}
              value={form.weeksToLookBack}
              onChange={updateField('weeksToLookBack')}
              error={errorFor('weeksToLookBack')}
              required
              data-testid="ufp-run-weeks"
            />
          </div>
          <div className="w-36">
            <Input
              name="ufpRunApiVersion"
              label={t('form.apiVersion')}
              type="number"
              min={1}
              max={RUN_REQUEST_LIMITS.maxApiVersion}
              value={form.apiVersion}
              onChange={updateField('apiVersion')}
              error={errorFor('apiVersion')}
              required
              data-testid="ufp-run-api-version"
            />
          </div>
          <div className="flex items-center gap-3 pt-7">
            <Button
              width="auto"
              loading={isStarting}
              disabled={isStarting || runInProgress}
              onClick={handleRun}
              data-testid="ufp-run-submit"
            >
              {t('form.run')}
            </Button>
            {runInProgress && (
              <span className="text-xs text-neut-sec">
                {t('form.runUnavailable')}
              </span>
            )}
          </div>
        </div>
      </SectionPanel>

      {runId !== undefined && <FollowedRun followed={followed} runId={runId} />}
    </div>
  );
}
